import { Directive, OnInit, Output, Input, inject } from "@angular/core";
import { CategoryContextFacade, CategoryContext } from "ish-core/facades/category-context.facade";
import { ReplaySubject, combineLatest, startWith, distinctUntilChanged, pairwise, debounceTime } from "rxjs";
declare type IdType = number | string;

@Directive({
  selector: '[ishCategoryContext]',
  providers: [CategoryContextFacade],
  exportAs: 'ishCategoryContext',
})
export class CategoryContextDirective implements OnInit {
  private context = inject(CategoryContextFacade);

  @Output() categoryRefChange = this.context.select('categoryRef');

  private id$ = new ReplaySubject<IdType>(1);

  constructor() {
    const parentContext = inject(CategoryContextFacade, { skipSelf: true, optional: true })!;

    if (parentContext) {
      parentContext.connect(
        'children',
        combineLatest([
          this.id$.pipe(startWith(), distinctUntilChanged(), pairwise()),
          this.context.select().pipe(debounceTime(0)),
        ]),
        (parent, [[prevId, currId], context]) => {
          let newChildren: CategoryContext['children'];

          if (context && prevId !== currId) {
            newChildren = { ...parent.children };
            delete newChildren?.[prevId];
          }

          if (context && currId != null) {
            newChildren ??= { ...parent.children };
            newChildren[currId] = context;
          }
          return newChildren ?? parent.children;
        }
      );
    }
  }

  @Input()
  set log(log: boolean) {
    this.context.log(log);
  }
  
  @Input() set uniqueId(value: string) {
    this.context.set('uniqueId', () => value);
    this.id$.next(value);
  }
 

  @Input() set categoryRef(value: string) {
    this.context.set('categoryRef', () => value);
  }

  @Input() set categoryPath(value: string[]) {
    this.context.set('categoryPath', () => value);
  }

  @Input() set propagateIndex(value: string) {
    this.id$.next(value);
  }

  @Input() set propagateActive(value: boolean) {
    this.context.set('hasChildren', () => value);
  }

  ngOnInit(): void {
    this.context.set('loading', () => false);
  }
}
